import React from 'react';
import { motion } from 'framer-motion';
import { Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Upload, Cpu, Bell, ChevronRight, Rocket, Check, Terminal,
} from 'lucide-react';
import Starfield from '../components/Starfield';
import Header from '../components/Header';
import HeroStats from '../components/HeroStats';
import { useAuth } from '../contexts/AuthContext';
import { computeCredits, formatInstance } from '../services/instances';
import { PUBLIC_TIERS, formatBytes } from '../services/plan';
import { getStats, ShowcaseJob } from '../services/api';
import PricingTable from '../components/PricingTable';

// PLACEHOLDER: ESA/Hubble's iconic 2006 M42 (Orion Nebula) mosaic
// (heic0601a), served straight from the ESA Hubble CDN. Public domain via
// NASA/ESA. Swap this for our own stacked hero image (or a rotating gallery
// of user submissions) before launch — hotlinking a foreign CDN in prod is
// fine for a beta but not polite forever.
const HERO_IMG =
  'https://cdn.esahubble.org/archives/images/wallpaper5/heic0601a.jpg';
const HERO_ATTRIBUTION = 'M42 · NASA / ESA / Hubble';

const Home: React.FC = () => {
  const { isAuthenticated, signIn } = useAuth();

  // Authenticated visitors have nothing to do on the marketing page.
  // Bounce them to /dashboard so the landing URL and the logo agree.
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <div className="relative bg-space-950">
      {/* Route-scoped SEO metadata. Duplicates the values baked into
          index.html so Google's JS-rendered snapshot matches the initial
          HTML (crawler consistency = ranking signal). Different values
          per route on other pages via their own Helmet blocks. */}
      <Helmet>
        <title>Seestar Cloud Stacking &amp; Backup | PancakeStack</title>
        <meta
          name="description"
          content="Cloud stacking and backup for Seestar. Stacks up to 500 frames are free; larger stacks and drizzle 2× use credits. Features: mosaic detection, frame filtering, stack comparison, Google Drive import."
        />
        <link rel="canonical" href="https://pancakestack.net/" />
      </Helmet>

      {/* ================ HERO ================ */}
      <section className="relative min-h-screen flex flex-col">
        {/* The photo. `fixed`-ish look via absolute + object-cover fills the fold
            without paying the layout-thrash cost of an actual fixed background. */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={HERO_IMG}
            alt="The Orion Nebula (M42) — Hubble Space Telescope mosaic"
            className="h-full w-full object-cover object-center scale-105 animate-fade-in"
            loading="eager"
          />
          {/* Flat darken layer so text pops uniformly, then vignette + bottom
              fade for edge legibility and section blend. */}
          <div className="absolute inset-0 bg-space-950/45" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_20%,rgba(5,6,13,0.55)_75%,#05060d_100%)]" />
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-space-950 via-space-950/70 to-transparent" />
        </div>

        {/* Header rides on top of the photo. */}
        <div className="relative z-20">
          <Header />
        </div>

        {/* Copy pinned bottom-left, generous whitespace above. */}
        <div className="relative z-10 mt-auto px-6 md:px-12 pb-16 md:pb-24 max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            className="max-w-3xl"
          >
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/50 text-emerald-100 text-xs font-semibold backdrop-blur uppercase tracking-wider">
                500 frames free
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-black/40 border border-white/15 text-white/80 text-xs font-medium backdrop-blur">
                Seestar only · by design
              </div>
              <a
                href="https://siril.org"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-aurora-400/15 border border-aurora-400/40 text-aurora-200 text-xs font-medium backdrop-blur hover:bg-aurora-400/25 transition-colors"
              >
                Powered by Siril
              </a>
            </div>

            <h1
              className="font-display text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[1.02] mb-6"
              style={{ textShadow: '0 4px 40px rgba(0,0,0,0.6)' }}
            >
              Seestar
              <br />
              <span className="bg-gradient-to-r from-nebula-200 via-aurora-400 to-nebula-300 bg-clip-text text-transparent">
                cloud stacking &amp; backup
              </span>
            </h1>

            <p
              className="text-lg md:text-xl text-white/85 leading-relaxed mb-10 max-w-2xl"
              style={{ textShadow: '0 2px 20px rgba(0,0,0,0.7)' }}
            >
              Access <span className="text-aurora-300 font-semibold">all your Seestar data</span>,
              wherever you are.{' '}
              <span className="text-aurora-300 font-semibold">Never delete a session</span> —
              never wait on a workstation.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              {isAuthenticated ? (
                <Link
                  to="/dashboard"
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-nebula-500 hover:bg-nebula-400 text-white font-semibold shadow-nebula-lg transition-all"
                >
                  See your dashboard
                  <ChevronRight className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              ) : (
                <button
                  onClick={signIn}
                  className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-nebula-500 hover:bg-nebula-400 text-white font-semibold shadow-nebula-lg transition-all"
                >
                  <Rocket className="h-5 w-5" />
                  Sign in to get started
                </button>
              )}
              <a
                href="#features"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white/[0.04] hover:bg-white/10 backdrop-blur text-white/80 font-medium border border-white/10 transition-colors"
              >
                See it in action
              </a>
            </div>
          </motion.div>

          {/* Live stat boxes — above-the-fold community counters. Lives
              outside the max-w-3xl copy column so it can span the full
              hero workable width. Hides gracefully if /stats is
              unreachable or the table is cold. */}
          <HeroStats />
        </div>

        {/* Attribution — small, corner, un-shouty. */}
        <div className="absolute bottom-3 right-4 z-10 text-[10px] uppercase tracking-widest text-white/40 font-mono">
          {HERO_ATTRIBUTION}
        </div>
      </section>

      {/* ================ REST OF PAGE (over starfield) ================ */}
      <div className="relative">
        <Starfield />
        <div className="relative z-10">

          {/* FEATURE-FORWARD ORDER (2026-08 subscriptions launch):
              features → how → pricing → build-on-top → CTA. Users buy
              outcomes, not prices. */}

          {/* REAL STACKS — numbers-only proof strip. Frames, drizzle,
              hardware, runtime, integration for the most-recent succeeded
              jobs. No user labels, target names, or thumbnails. Silent
              when the /stats endpoint is unreachable or the showcase
              array is empty (fresh install). First section under the
              hero — social proof before feature list. */}
          <RecentStacksSection />

          {/* SEE IT IN ACTION — the four differentiators as full-bleed
              alternating rows. Each row is one screenshot + one caption +
              one supporting sentence. Glanceable per the danesh #1
              principle: the image is the story, prose is the caption. */}
          <section id="features" className="relative px-6 md:px-10 py-20 md:py-28 max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16 md:mb-20"
            >
              <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
                Features.
              </h2>
              <p className="text-white/60 max-w-xl mx-auto">
                What PancakeStack does with your Seestar sessions.
              </p>
            </motion.div>

            <div className="space-y-24 md:space-y-32">
              {FEATURES.map((f, i) => (
                <FeatureRow key={f.title} feature={f} index={i} />
              ))}
            </div>
          </section>

          {/* HOW IT WORKS */}
          <section id="how" className="relative px-6 md:px-10 py-24 max-w-6xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="font-display text-3xl md:text-4xl font-bold mb-3 text-center"
            >
              How it works.
            </motion.h2>
            <p className="text-center text-white/60 mb-16 max-w-xl mx-auto">
              Upload, stack, download.
            </p>

            <div className="grid md:grid-cols-3 gap-6">
              {STEPS.map((s, i) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="relative p-7 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur hover:bg-white/[0.05] transition-colors"
                >
                  <div className="absolute -top-3 -left-3 h-8 w-8 rounded-full bg-nebula-500 text-white font-semibold flex items-center justify-center text-sm shadow-nebula">
                    {i + 1}
                  </div>
                  <s.icon className="h-8 w-8 text-nebula-300 mb-4" />
                  <h3 className="font-display text-lg font-semibold mb-2">{s.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed">{s.body}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* WHY */}
          <section className="relative px-6 md:px-10 py-20 max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="font-display text-3xl md:text-4xl font-bold mb-5">
                  Siril in the cloud, tuned for Seestar.
                </h2>
                <p className="text-white/70 leading-relaxed mb-6">
                  Seestar sessions are folders of pre-calibrated FITS — no
                  darks, flats, or biases required. PancakeStack ships two
                  Siril scripts tuned for the Seestar one-shot color
                  sensor. Every uploaded frame is stored so a collection
                  can be re-stacked from any device. Drizzle 2× runs on a
                  larger instance in one pass.
                </p>
                <ul className="space-y-3 text-sm text-white/70">
                  {BULLETS.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-aurora-400" />
                      {b}
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <div className="absolute -inset-8 bg-nebula-500/20 blur-3xl rounded-full" />
                <div className="relative p-6 rounded-2xl bg-space-900/80 border border-white/10 backdrop-blur font-mono text-xs leading-relaxed text-white/80 shadow-nebula">
                  <div className="text-white/40 mb-2"># one-time — install the CLI</div>
                  <div><span className="text-nebula-300">$</span> brew install glennbech/tap/pancakestack</div>

                  <div className="text-white/40 mt-4 mb-2"># sign in with Google (opens your browser)</div>
                  <div><span className="text-nebula-300">$</span> pancakestack login</div>

                  <div className="text-white/40 mt-4 mb-2"># upload one night's Seestar folder</div>
                  <div><span className="text-nebula-300">$</span> pancakestack upload m81 ~/Seestar/M81</div>

                  <div className="text-white/40 mt-4 mb-2"># stack it in the cloud</div>
                  <div><span className="text-nebula-300">$</span> pancakestack stack m81 --script seestar</div>
                  <div className="mt-3 text-white/50">✓ stacking m81 — follow along at pancakestack.net/jobs</div>
                  <div className="text-aurora-400">✓ we'll ping you when your master's ready</div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* WHAT IT COSTS — table + calculator. Comparison table
              (PricingTable) up top so visitors see the shape of the
              tiers before playing with the calculator. */}
          <PricingCallout />

          {/* API PITCH — sit right before the final CTA. Positions the
              public API as "build on top of pancakestack" for anyone with
              scripts, notebooks, or scope-automation workflows. Same PAT
              auth as the CLI, same endpoints as the webapp — no private
              back-channel. Link the Scalar-rendered reference at /api-docs. */}
          <BuildOnTopSection />

          {/* FINAL CTA */}
          <section className="relative px-6 md:px-10 py-24 max-w-4xl mx-auto text-center">
            <motion.h2
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="font-display text-4xl md:text-5xl font-bold mb-5"
            >
              Get started.
            </motion.h2>
            <p className="text-white/60 mb-8">
              Free during closed beta. Google sign-in. No credit card required.
            </p>
            {!isAuthenticated && (
              <button
                onClick={signIn}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-nebula-500 hover:bg-nebula-400 text-white font-semibold shadow-nebula-lg transition-all"
              >
                <Rocket className="h-5 w-5" />
                Sign in and start stacking
              </button>
            )}
          </section>

          <footer className="relative z-10 border-t border-white/5 py-6 text-center text-xs text-white/40">
            Powered by Siril &middot;{' '}
            <a
              href="https://github.com/glennbech/siril-cloud"
              className="hover:text-white/70 transition-colors"
              target="_blank" rel="noreferrer"
            >
              source
            </a>{' '}
            &middot;{' '}
            <Link to="/credits" className="hover:text-white/70 transition-colors">
              credits &amp; licenses
            </Link>
          </footer>
        </div>
      </div>
    </div>
  );
};

// RecentStacksSection — pulls /stats.showcase and renders each entry as
// a compact numeric row: frames · drizzle · hardware · runtime ·
// integration. No target names, no user labels, no images — Glenn's
// call, and the point of the strip is "real workloads run here," not
// "look at this pretty M42." Silent when the endpoint fails or the
// showcase list is empty (fresh install, cached row from before the
// showcase field shipped, etc.).
const RECENT_STACKS_COLLAPSED = 5;

const RecentStacksSection: React.FC = () => {
  const [rows, setRows] = React.useState<ShowcaseJob[] | null>(null);
  const [expanded, setExpanded] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    getStats()
      .then((s) => { if (!cancelled) setRows(s.showcase ?? []); })
      .catch(() => { if (!cancelled) setRows([]); });
    return () => { cancelled = true; };
  }, []);

  if (rows === null || rows.length === 0) return null;

  const visible = expanded ? rows : rows.slice(0, RECENT_STACKS_COLLAPSED);
  const hasMore = rows.length > RECENT_STACKS_COLLAPSED;

  return (
    <section className="relative px-6 md:px-10 py-20 md:py-24 max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
        className="text-center mb-10 md:mb-14"
      >
        <div className="text-[10px] uppercase tracking-widest text-aurora-300/80 font-semibold mb-3">
          Live from the cluster
        </div>
        <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
          Recent stacks.
        </h2>
        <p className="text-white/60 max-w-xl mx-auto text-sm">
          Newest jobs first, straight off the queue. Nothing filtered.
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden"
      >
        {/* Header row — desktop only; mobile stacks the values so a
            header would just be dead pixels there. */}
        <div className="hidden md:grid grid-cols-[1.1fr_0.7fr_1.4fr_0.9fr_0.9fr] gap-4 px-5 py-3 text-[10px] uppercase tracking-widest text-white/40 font-semibold border-b border-white/[0.06] bg-white/[0.02]">
          <div>Frames</div>
          <div>Drizzle</div>
          <div>Hardware</div>
          <div className="text-right">Runtime</div>
          <div className="text-right">Integration</div>
        </div>
        <ul className="divide-y divide-white/[0.06]">
          {visible.map((j, i) => (
            <RecentStackRow key={i} job={j} />
          ))}
        </ul>
        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full py-3 text-xs uppercase tracking-widest font-semibold text-white/50 hover:text-white hover:bg-white/[0.03] border-t border-white/[0.06] transition-colors"
          >
            {expanded
              ? 'Show fewer'
              : `Show ${rows.length - RECENT_STACKS_COLLAPSED} more`}
          </button>
        )}
      </motion.div>
    </section>
  );
};

const RecentStackRow: React.FC<{ job: ShowcaseJob }> = ({ job }) => {
  const drizzle = job.drizzle && job.drizzle > 1 ? `${job.drizzle}×` : '—';
  const runtime = job.runtimeSeconds ? formatDuration(job.runtimeSeconds) : '—';
  const integration = job.integrationSeconds ? formatDuration(job.integrationSeconds) : '—';
  const instance = formatInstance(job.instanceType);
  return (
    <li className="md:grid md:grid-cols-[1.1fr_0.7fr_1.4fr_0.9fr_0.9fr] md:gap-4 md:items-baseline px-5 py-4 hover:bg-white/[0.02] transition-colors">
      {/* Desktop cells */}
      <div className="hidden md:block font-display font-semibold text-white tabular-nums">
        {job.frameCount.toLocaleString('en-US')}
      </div>
      <div className={`hidden md:block tabular-nums ${job.drizzle && job.drizzle > 1 ? 'text-aurora-300 font-semibold' : 'text-white/40'}`}>
        {drizzle}
      </div>
      <div className="hidden md:block text-white/70 font-mono text-sm truncate">
        {instance || '—'}
      </div>
      <div className="hidden md:block text-right text-white/70 tabular-nums font-mono text-sm">
        {runtime}
      </div>
      <div className="hidden md:block text-right text-white/70 tabular-nums font-mono text-sm">
        {integration}
      </div>

      {/* Mobile stacked layout — same fields, labelled inline */}
      <div className="md:hidden">
        <div className="font-display font-semibold text-white text-lg tabular-nums mb-2">
          {job.frameCount.toLocaleString('en-US')} <span className="text-white/40 text-sm font-normal">frames</span>
          {job.drizzle && job.drizzle > 1 && (
            <span className="ml-2 text-aurora-300 text-sm">· drizzle {job.drizzle}×</span>
          )}
        </div>
        <div className="text-xs text-white/50 space-y-1 font-mono">
          <div>Hardware: <span className="text-white/70">{instance || '—'}</span></div>
          <div className="flex gap-4">
            <span>Runtime: <span className="text-white/70 tabular-nums">{runtime}</span></span>
            <span>Integration: <span className="text-white/70 tabular-nums">{integration}</span></span>
          </div>
        </div>
      </div>
    </li>
  );
};

// formatDuration — same shape as the rest of the app: "2 h 14 min" for
// >= 1h, "34 min" for anything less. Below a minute (rare — instance
// spin-up alone is ~30s) rounds up to "1 min" so no row reads as "0 min"
// which looks like a bug.
const formatDuration = (totalSec: number): string => {
  const h = Math.floor(totalSec / 3600);
  const m = Math.round((totalSec % 3600) / 60);
  if (h === 0) return m <= 0 ? '1 min' : `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
};

// BuildOnTopSection — the "build on top of PancakeStack" pitch for
// hackers with notebooks / scope-automation / their own tooling. The
// key claim is parity: every capability the webapp and CLI have goes
// through the same public API, no private endpoints, no shape drift.
// The Scalar-rendered reference at /api-docs is the destination.
const BuildOnTopSection: React.FC = () => (
  <section className="relative px-6 md:px-10 py-24 max-w-6xl mx-auto">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.6 }}
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-space-900/80 via-space-950/60 to-nebula-900/30 p-8 md:p-12 backdrop-blur"
    >
      {/* Nebula halo behind the icon so the title reads with weight. */}
      <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full bg-nebula-500/20 blur-3xl pointer-events-none" />

      <div className="relative grid md:grid-cols-[1fr_auto] gap-10 items-start">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full bg-nebula-500/15 border border-nebula-400/30 text-xs uppercase tracking-widest font-semibold text-nebula-200">
            <Terminal className="h-3.5 w-3.5" />
            Public API
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4 leading-tight">
            Build on top of PancakeStack.
          </h2>
          <p className="text-white/70 leading-relaxed mb-6 max-w-2xl">
            Everything the web app and CLI do is available via the public API.
            Mint a personal access token and call the endpoints directly from
            scripts, notebooks, or automation.
          </p>

          <ul className="space-y-2.5 mb-8 max-w-2xl">
            {[
              <><span className="text-white font-semibold">Full parity.</span> <span className="text-white/60">The web app and CLI use the same endpoints.</span></>,
              <><span className="text-white font-semibold">PAT authentication.</span> <span className="text-white/60">Same token model as the <code className="font-mono text-aurora-300">pancakestack</code> CLI.</span></>,
              <><span className="text-white font-semibold">OpenAPI reference.</span> <span className="text-white/60">Generated from the API source.</span></>,
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check className="h-4 w-4 mt-1 shrink-0 text-nebula-300" />
                <div className="text-sm md:text-base">{item}</div>
              </li>
            ))}
          </ul>

          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/api-docs"
              className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-nebula-500 hover:bg-nebula-400 text-white font-semibold shadow-nebula transition-all"
            >
              See the API reference
              <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <a
              href="https://github.com/glennbech/pancakestack-cli"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-white/[0.04] hover:bg-white/10 backdrop-blur text-white/80 font-medium border border-white/10 transition-colors"
            >
              CLI on GitHub
            </a>
          </div>
        </div>

        {/* Terminal-flavored teaser on the right. Same visual language as
            the Made-for-Seestar CLI snippet so the API and CLI feel like
            two doors into the same house. */}
        <div className="relative w-full md:w-auto md:min-w-[320px]">
          <div className="p-5 rounded-2xl bg-space-950/80 border border-white/10 backdrop-blur font-mono text-xs leading-relaxed text-white/80 shadow-nebula">
            <div className="text-white/40 mb-2"># start a stack from anything that speaks HTTP</div>
            <div><span className="text-nebula-300">$</span> curl -X POST \\</div>
            <div className="pl-4">https://api.pancakestack.net/jobs \\</div>
            <div className="pl-4">-H <span className="text-aurora-300">"Authorization: Bearer $PAT"</span> \\</div>
            <div className="pl-4">-H <span className="text-aurora-300">"Content-Type: application/json"</span> \\</div>
            <div className="pl-4">-d <span className="text-aurora-300">'{'{'}"collectionId":"m81",</span></div>
            <div className="pl-8"><span className="text-aurora-300">"scriptId":"seestar-advanced"{'}'}'</span></div>
            <div className="mt-3 text-white/50">✓ jobId: 9a34…, launched</div>
            <div className="text-aurora-400">✓ follow along at pancakestack.net/jobs</div>
          </div>
        </div>
      </div>
    </motion.div>
  </section>
);

// PricingCallout — "what it costs" section. Kicker + comparison table
// (PricingTable) + interactive calculator. The table is the shape of
// the offering; the calculator is the "prove it to yourself" toy.
// Copy uses "credits" (matches Profile ledger terminology).
const PricingCallout: React.FC = () => (
  <section className="relative px-6 md:px-10 py-20 md:py-24 max-w-5xl mx-auto">
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.6 }}
      className="text-center mb-10"
    >
      <h2 className="font-display text-3xl md:text-4xl font-bold mb-3">
        What it costs.
      </h2>
      <p className="text-white/60 max-w-xl mx-auto">
        Free up to 500 frames per stack. Above that, you spend credits.
      </p>
    </motion.div>

    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.6 }}
    >
      <PricingTable />
    </motion.div>

    <div className="mt-10">
      <PricingCalculator />
    </div>

    <div className="mt-6 text-center text-xs text-white/40">
      Prices exclude local tax — collected at checkout by Stripe.
    </div>
  </section>
);

// PricingCalculator — interactive toy for the landing page. Slider +
// drizzle toggle → live credit cost. Uses computeCredits from
// services/instances so the display never drifts from what the backend
// actually charges. Default 500 frames + non-drizzle lands on "Free"
// which is the whole psychological win of the box.
// Default Seestar exposure length. Almost everyone shoots 30s subs
// (10s for the moon, 60s for planetary — 30s is the mainline DSO
// setting). Hardcoded for the calculator; if a shopper wants to model
// 10s subs they can divide the integration in their head.
const CALCULATOR_EXPOSURE_SEC = 30;

const formatIntegration = (totalSec: number): string => {
  const h = Math.floor(totalSec / 3600);
  const m = Math.round((totalSec % 3600) / 60);
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
};

const PricingCalculator: React.FC = () => {
  const [frames, setFrames] = React.useState(500);
  const [drizzle, setDrizzle] = React.useState(false);
  const credits = computeCredits(frames, drizzle ? 2 : 1);
  const isFree = credits === 0;
  const integration = formatIntegration(frames * CALCULATOR_EXPOSURE_SEC);

  return (
    <div className="mt-6 p-5 rounded-2xl bg-space-950/60 border border-white/10 backdrop-blur">
      <div className="text-xs uppercase tracking-widest text-nebula-300 font-semibold mb-4">
        Try it
      </div>

      <div className="flex items-baseline justify-between mb-2">
        <label htmlFor="frame-slider" className="text-sm text-white/70">
          Frames <span className="text-white/40 text-xs ml-1">@ 30s exposure</span>
        </label>
        <span className="text-2xl font-display font-bold tabular-nums text-white">
          {frames.toLocaleString('en-US')}
        </span>
      </div>
      <input
        id="frame-slider"
        type="range"
        min={100}
        max={7000}
        step={50}
        value={frames}
        onChange={(e) => setFrames(Number(e.target.value))}
        className="w-full accent-nebula-400 cursor-pointer"
      />
      <div className="flex justify-between items-baseline text-[10px] text-white/40 mt-1 mb-5 uppercase tracking-widest font-mono">
        <span>100</span>
        <span className="normal-case tracking-normal text-aurora-300/80 text-xs font-sans tabular-nums">
          = {integration} integration
        </span>
        <span>7000</span>
      </div>

      <label className="flex items-center gap-2.5 mb-5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={drizzle}
          onChange={(e) => setDrizzle(e.target.checked)}
          className="h-4 w-4 accent-aurora-400"
        />
        <span className="text-sm text-white/80">
          Drizzle 2×
          <span className="text-white/40 ml-2 text-xs">
            (needs 300+ GB RAM working set)
          </span>
        </span>
      </label>

      <div className="pt-4 border-t border-white/10 flex items-baseline justify-between">
        <span className="text-sm text-white/60">
          {isFree ? 'Cost' : 'This stack costs'}
        </span>
        {isFree ? (
          <span className="text-3xl font-display font-bold bg-gradient-to-b from-emerald-200 to-emerald-400 bg-clip-text text-transparent leading-none">
            Free
          </span>
        ) : (
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-display font-bold tabular-nums text-white leading-none">
              {credits.toLocaleString('en-US')}
            </span>
            <span className="text-sm text-white/50">
              credit{credits === 1 ? '' : 's'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// FeatureRow — one differentiator: a screenshot on one side, terse
// copy on the other. Alternates left/right by index so the eye
// snakes down the page without hitting a wall of screenshots.
const FeatureRow: React.FC<{
  feature: FeatureShape;
  index: number;
}> = ({ feature, index }) => {
  const flipped = index % 2 === 1;
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.6 }}
      className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center ${flipped ? 'md:[&>div:first-child]:order-2' : ''}`}
    >
      {/* Screenshot side. Subtle rounded frame + shadow-nebula halo
          so the images read as artifacts of the product, not stock
          photos. */}
      <div className="relative group">
        <div className="absolute -inset-4 md:-inset-6 bg-nebula-500/15 blur-3xl rounded-3xl pointer-events-none opacity-70 group-hover:opacity-100 transition-opacity" />
        <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-space-900/60">
          <img
            src={feature.image}
            alt={feature.alt}
            className="block w-full h-auto"
            loading="lazy"
          />
        </div>
      </div>

      {/* Copy side. Kicker (uppercase micro), h3, one paragraph. */}
      <div>
        <div className="text-xs uppercase tracking-widest text-nebula-300 font-semibold mb-3">
          {feature.kicker}
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-bold mb-4 leading-tight">
          {feature.title}
        </h3>
        <p className="text-white/70 leading-relaxed mb-4">
          {feature.body}
        </p>
        {feature.detail && (
          <p className="text-sm text-white/50 leading-relaxed">
            {feature.detail}
          </p>
        )}
      </div>
    </motion.div>
  );
};

interface FeatureShape {
  kicker: string;
  title: string;
  body: string;
  detail?: string;
  image: string;
  alt: string;
}

const FEATURES: FeatureShape[] = [
  {
    kicker: 'Panalyzer',
    title: 'Mosaic detection and per-panel quality.',
    body:
      'See how the individual frames align in a mosaic. Every frame plots on ' +
      'a right-ascension scatter; dense clusters are identified as panels. ' +
      'Per-panel quality metrics (star count, focus, background) are computed ' +
      'separately for each panel.',
    detail: 'Select which panels to include in the stack.',
    image: '/marketing/panalyzer.png',
    alt: 'Panalyzer — right-ascension scatter with two dense panels highlighted, per-panel quality metrics on the right',
  },
  {
    kicker: 'Split-stacking',
    title: 'Fire off multiple stacks in parallel.',
    body:
      'Group frames by exposure, filter, and/or telescope. Each distinct ' +
      'combination becomes its own stack, launched in parallel and grouped ' +
      'under one batch. Compare exposure lengths, isolate the LP filter ' +
      'run, or split S30 from S50 frames without re-uploading.',
    detail: 'Paid-plan feature — free tier stacks one job at a time.',
    image: '/marketing/split-stacking.png',
    alt: 'Split-stacking dialog — five buckets grouped by exposure and filter, per-bucket frame counts and credit costs',
  },
  {
    kicker: 'Filter frames',
    title: 'Frame filtering by quality metric.',
    body:
      'Every FITS is analyzed on upload for FWHM, eccentricity, SNR, star count, ' +
      'background, and PA. Histograms across the whole session let you set an ' +
      'inclusion range on each metric before running a stack.',
    detail: 'Frames without metadata can be included or excluded with a checkbox.',
    image: '/marketing/filter-frames.png',
    alt: 'Filter frames — histograms for FWHM, eccentricity, SNR, stars, background, PA, exposure buckets with range sliders',
  },
  {
    kicker: 'Compare stacks',
    title: 'Side-by-side stack comparison.',
    body:
      'Two runs on the same collection (different scripts, different drizzle values) ' +
      'can be loaded into a slider view. Drag the divider to see the pixel-level ' +
      'difference between them.',
    detail: 'Past stacks remain available. Re-run and re-compare later.',
    image: '/marketing/compare-stacks.png',
    alt: 'Compare stacks — side-by-side slider between a seestar and seestar-advanced stack of Cygnus 457',
  },
  {
    kicker: 'Sky map',
    title: 'Plan tonight, size the frame.',
    body:
      'Browse the sky to find what clears your horizon and lay the Seestar ' +
      'field of view over any target. Every Seestar scope — S30, S30 Pro, ' +
      'S30 Pro Plus, S50, S50 Pro — is a click to compare framing before ' +
      'you point.',
    detail: 'Drag the FOV rectangle to preview mosaic panels.',
    image: '/marketing/skymap.png',
    alt: 'Sky map — M31 Andromeda Galaxy with framed field-of-view rectangles for S30, S30 Pro, S30 Pro Plus, S50, S50 Pro',
  },
  {
    kicker: 'Google Drive',
    title: 'Import from Google Drive.',
    body:
      'Paste a Google Drive share URL. The backend fetches the archive or folder ' +
      'of FITS into your collection. No local upload step.',
    detail: 'Drive items must be shared as "Anyone with the link." Folder imports are flat.',
    image: '/marketing/drive-import.png',
    alt: 'New collection page — Google Drive import tab with a share-URL input field',
  },
];

const STEPS: {
  icon: typeof Upload;
  title: string;
  body: React.ReactNode;
}[] = [
  {
    icon: Upload,
    title: 'Upload the session',
    body: (
      <>
        Drop the .fit files individually or as an archive. Upload progress
        is shown. Larger sessions can use the{' '}
        <a
          href="https://github.com/glennbech/pancakestack-cli"
          target="_blank"
          rel="noreferrer"
          className="text-aurora-300 underline decoration-aurora-400/40 underline-offset-4 hover:decoration-aurora-300"
        >
          CLI
        </a>{' '}
        instead.
      </>
    ),
  },
  {
    icon: Cpu,
    title: 'Stacking runs',
    body: 'Siril runs the script you selected. Progress is visible on the stacks page.',
  },
  {
    icon: Bell,
    title: 'Notification on completion',
    body: 'An email arrives when the stack finishes. The preview and full FITS master are on the stack page.',
  },
];

const BULLETS = [
  'Seestar defaults — filter roundness, rejection, RGB equalize',
  '2× drizzle in one pass — no batching, no RAM ceiling',
  'Cloud backup of every uploaded FITS — re-stack from any device',
  'Auto plate-solve + per-frame quality metrics (FWHM, PA, SNR)',
  'Instance size auto-picked from workload — pay for what you use',
];

export default Home;
